import {assignInlineVars} from '@vanilla-extract/dynamic';
import cx from 'classnames';

import {Text} from '#component/base';
import {sprinkles} from '#style/sprinkles';
import {minimumScores} from '#util/lifestyleScore';

import {LifestyleMeterProps} from './LifestyleMeter.types';

import * as css from './LifestyleMeter.css';

const segments = [
  {info: 'Less than 50', label: 'Needs work'},
  {info: '51–69', label: 'Fair'},
  {info: '70–85', label: 'Good'},
  {info: '86–100', label: 'Great'},
];

export const LifestyleMeter = ({
  className,
  value = 0,
  maxValue = 100,
  withGlow = false,
  withInfo = false,
  withHighlight = false,
  ...otherProps
}: LifestyleMeterProps) => {
  const inactiveWidth = `${(1 - value / maxValue) * 100}%`;
  let activePosition: 0 | 1 | 2 | 3 = 0;

  if (value < minimumScores.low) {
    activePosition = 0;
  } else if (value < minimumScores.mid) {
    activePosition = 1;
  } else if (value < minimumScores.high) {
    activePosition = 2;
  } else {
    activePosition = 3;
  }

  return (
    <div
      className={cx(
        sprinkles({dir: ['col', 'colReverse'], flex: 'flex', gap: 8, pos: 'relative'}),
        className,
      )}
      style={assignInlineVars({[css.inactiveWidthVar]: inactiveWidth})}
      {...otherProps}>
      {withGlow && <div className={css.glow} />}
      <div className={sprinkles({flex: 'flex', pos: 'relative'})}>
        {segments.map(({label, info}, i) => {
          const active = withHighlight && activePosition === i;

          return (
            <div className={css.label({index: i as 0 | 1 | 2 | 3})} key={i}>
              <Text
                color={active ? 'deepBurgundy' : 'doveGray'}
                fontWeight={active ? 'semiBold' : 'medium'}
                variant="caption">
                {label}
              </Text>

              {withInfo && (
                <Text color="doveGray" fontWeight="regular" variant="caption">
                  {info}
                </Text>
              )}
            </div>
          );
        })}
      </div>

      <div
        aria-label="Lifestyle Score"
        aria-valuemax={maxValue}
        aria-valuemin={0}
        aria-valuenow={value}
        className={css.progressContainer}
        role="meter">
        {segments.map((_, i) => (
          <div className={css.progressSegment({index: i as 0 | 1 | 2 | 3})} key={i} />
        ))}
      </div>
    </div>
  );
};
